import React, { useState } from 'react'
import { render } from '@react-email/components'
import {
  Html,
  Head,
  Preview,
  Body,
  Container,
  Section,
  Text,
  Button as EmailButton,
  Hr,
} from '@react-email/components'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { toast } from '@/hooks/use-toast'
import { Save, Eye, Undo2 } from 'lucide-react'

export type EmailDesignerProps = {
  initialDesignJson?: any
  initialSubject?: string
  onCancel: () => void
  onSave: (payload: { subject: string; html: string; designJson: any }) => void
}

const EmailDesigner: React.FC<EmailDesignerProps> = ({
  initialDesignJson,
  initialSubject,
  onCancel,
  onSave,
}) => {
  const [subject, setSubject] = useState(initialSubject || 'Sınav Daveti')
  const [emailContent, setEmailContent] = useState(
    initialDesignJson?.content ||
      `Sayın {EMPLOYEE_NAME},

FW25 terfi süreci kapsamında tarafınıza {TEST_NAME} sınavı atanmıştır. Sınav, NovaForge platformu üzerinden çevrim içi uygulanacaktır.
Giriş Linki: {LOGIN_LINK}
Sınav Detayları:
• Sınav Adı: {TEST_NAME}
• Erişim Kodu: {ACCESS_CODE}
• Başlangıç Tarihi: {START_DATE}
• Son Giriş Tarihi: {DUE_DATE}
• Sınav Süresi: {TIME_LIMIT}

Başarılar dileriz!`
  )
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')

  const subjectLimit = 120
  const subjectTooLong = subject.length > subjectLimit

  const MERGE_TAGS = [
    { key: 'EMPLOYEE_NAME', sample: 'Ayşe Yılmaz' },
    { key: 'TEST_NAME', sample: 'Mağaza Satış Değerlendirmesi' },
    { key: 'ACCESS_CODE', sample: 'XY7-93K' },
    { key: 'START_DATE', sample: '01.09.2025 09:00' },
    { key: 'DUE_DATE', sample: '07.09.2025 16:00' },
    { key: 'TIME_LIMIT', sample: '30 dakika' },
    { key: 'LOGIN_LINK', sample: 'https://mavi.novaforge.tech/video-interview' },
  ]

  const fillSamples = (text: string) =>
    MERGE_TAGS.reduce(
      (acc, tag) => acc.replace(new RegExp(`\\{${tag.key}\\}`, 'g'), tag.sample),
      text
    )

  const buildEmailHtml = async (withSamples = false) => {
    const processText = (text: string) => (withSamples ? fillSamples(text) : text)
    const loginUrl = withSamples ? 'https://mavi.novaforge.tech/video-interview' : '{LOGIN_LINK}'

    // Convert plain text to HTML with proper formatting
    const contentLines = processText(emailContent).split('\n')
    const htmlContent = contentLines
      .map((line) => {
        if (line.trim() === '') return '<br/>'

        if (line.includes('Sınav Detayları:')) {
          const styledLine = line.replace('Sınav Detayları:', '<strong>Sınav Detayları:</strong>')
          return `<p style="margin: 8px 0;">${styledLine}</p>`
        }

        if (line.includes('Giriş Linki:')) {
          let styledLine = line.replace('Giriş Linki:', '<strong>Giriş Linki:</strong>')
          if (line.includes('{LOGIN_LINK}')) {
            const linkText = withSamples ? loginUrl : '{LOGIN_LINK}'
            styledLine = styledLine.replace(
              '{LOGIN_LINK}',
              `<a href="${loginUrl}" style="color: #2563eb; text-decoration: underline;">${linkText}</a>`
            )
          }
          return `<p style="margin: 8px 0;">${styledLine}</p>`
        }

        if (line.includes('Erişim Kodu:')) {
          let styledLine = line.replace('Erişim Kodu:', '<span>Erişim Kodu:</span>')
          if (line.includes('{ACCESS_CODE}')) {
            styledLine = styledLine.replace(
              '{ACCESS_CODE}',
              '<span style="color: #2563eb;">{ACCESS_CODE}</span>'
            )
          }
          return `<div style="margin: 4px 0;">${styledLine}</div>`
        }

        if (line.includes('tarafınıza') && line.includes('{TEST_NAME}')) {
          const styledLine = line.replace('{TEST_NAME}', '<strong>{TEST_NAME}</strong>')
          return `<p style="margin: 8px 0;">${styledLine}</p>`
        }

        if (line.startsWith('•')) return `<div style="margin: 4px 0;">${line}</div>`

        if (line.includes('{LOGIN_LINK}')) {
          const linkText = withSamples ? loginUrl : '{LOGIN_LINK}'
          const processedLine = line.replace(
            '{LOGIN_LINK}',
            `<a href="${loginUrl}" style="color: #2563eb; text-decoration: underline;">${linkText}</a>`
          )
          return `<p style="margin: 8px 0;">${processedLine}</p>`
        }

        return `<p style="margin: 8px 0;">${line}</p>`
      })
      .join('')

    const emailComponent = (
      <Html lang="tr">
        <Head />
        <Preview>{processText(subject)}</Preview>
        <Body style={{ background: '#f6f7fb', margin: 0, padding: '24px 0' }}>
          <Container
            style={{
              maxWidth: 560,
              margin: '0 auto',
              background: '#ffffff',
              borderRadius: 16,
              padding: 24,
            }}
          >
            <Section>
              <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
              <Hr style={{ borderColor: '#e5e7eb', marginTop: 24, marginBottom: 8 }} />
            </Section>
          </Container>
        </Body>
      </Html>
    )

    return await render(emailComponent)
  }

  const handleSave = async () => {
    if (!subject.trim()) {
      toast({ title: 'Konu gerekli', description: 'Lütfen konu giriniz.', variant: 'destructive' })
      return
    }
    try {
      const html = await buildEmailHtml()
      onSave({
        subject: subject.trim(),
        html,
        designJson: { content: emailContent, source: 'react-email-simple' },
      })
    } catch (error) {
      console.error('Email render error:', error)
      toast({
        title: 'Dışa aktarma başarısız',
        description: 'Lütfen tekrar deneyin.',
        variant: 'destructive',
      })
    }
  }

  const handlePreview = async () => {
    try {
      const html = await buildEmailHtml(true)
      setPreviewHtml(html)
      setPreviewOpen(true)
    } catch (error) {
      console.error('Preview render error:', error)
      toast({
        title: 'Önizleme başarısız',
        description: 'Lütfen tekrar deneyin.',
        variant: 'destructive',
      })
    }
  }

  const insertMergeTag = (tagKey: string) => {
    const tag = `{${tagKey}}`
    setEmailContent((prev) => prev + tag)
  }

  return (
    <div className="flex flex-col h-[85vh] bg-white border rounded-lg">
      {/* Subject input */}
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <Label htmlFor="subject" className="sr-only">
          Konu
        </Label>
        <Input
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="E-posta konusu"
          className={`h-9 ${subjectTooLong ? 'border-red-500' : ''}`}
        />
        <span className={`text-xs ${subjectTooLong ? 'text-red-600' : 'text-muted-foreground'}`}>
          {subject.length}/{subjectLimit}
        </span>
      </div>

      {/* Single content editor */}
      <div className="flex-1 flex flex-col p-4">
        <div className="mb-3">
          <Label className="text-sm font-medium">E-posta İçeriği</Label>
          <div className="flex flex-wrap gap-1 mt-2 mb-2">
            {MERGE_TAGS.map((tag) => (
              <button
                key={tag.key}
                onClick={() => insertMergeTag(tag.key)}
                className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                title={`Örnek: ${tag.sample}`}
              >
                {tag.key}
              </button>
            ))}
          </div>
        </div>

        <Textarea
          value={emailContent}
          onChange={(e) => setEmailContent(e.target.value)}
          className="flex-1 font-mono text-sm"
          placeholder="E-posta içeriğinizi buraya yazın..."
        />
      </div>

      {/* Action buttons */}
      <div className="px-3 py-2 border-t flex items-center gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={handlePreview}>
          <Eye className="h-4 w-4 mr-2" /> Önizleme
        </Button>
        <Button size="sm" onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" /> Kaydet
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <Undo2 className="h-4 w-4 mr-2" /> İptal
        </Button>
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Önizleme</DialogTitle>
            <DialogDescription>{subject}</DialogDescription>
          </DialogHeader>
          <div className="border rounded h-[60vh] overflow-auto bg-white">
            <iframe title="preview" className="w-full h-full" srcDoc={previewHtml} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default EmailDesigner
